import React, {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';

import cs from '@utils/cs';
import {formatDate} from '@utils/formatDate';
import {formatName} from '@utils/formatName';
import useCategoryIcon from '@hooks/useCategoryIcon';
import {Improvement} from '@components/SurveyEntry';

import tree from '@images/category-tree.png';

import classes from './styles';

export type SurveyDataType = {
  id: number | string;
  title: string;
  description: string;
  attachment: {
    id: string;
    media: string;
    mediaAsset: {
      lg: string;
      og: string;
      sm: string;
    };
}[];
  audioFile?: string | File;
  category: {id: string | number; title: string};
  modifiedAt: string;
  createdAt: string;
  location: {type: string; coordinates: [number, number]};
  sentiment: string;
  status: string;
  boundary: {type?: string; coordinates: [[[number, number][]]]} | null;
  improvement: string | null;
  region: {id: number};
  isTest: boolean;
  isPublic: boolean;
  createdBy: {id: string | number, firstName: string, lastName: string};
  project: {id: string | number; title: string, isAdmin: boolean}
};

interface Props {
  data: SurveyDataType[];
  setActiveIndex(i: number): void;
  setShowDetails(value: boolean): void;
  loading: boolean;
}

interface ItemProps {
  item: SurveyDataType;
  index: number;
  setIndex(i: number): void;
  setShowDetails(value: boolean): void;
}

const SurveyItem: React.FC<ItemProps> = ({
  item,
  index,
  setIndex,
  setShowDetails,
}) => {
  const navigate = useNavigate();
  const [categoryIcon] = useCategoryIcon(item?.category?.id);
  const handleClick = useCallback((id: string|number) => {
    navigate(`/surveys/${id}`);
    setIndex(index);
    setShowDetails(true);
  }, [index, navigate, setIndex, setShowDetails]);

  return (
    <tr className={classes.tableItemRow}>
      <td>
        <p className={classes.itemTitle}>{(item.title).substring(0, 35) || 'No Title Found.'}</p>
      </td>
      <td>
        <div className={classes.categoryWrapper}>
          <img
            src={categoryIcon || tree}
            alt='category'
            className={classes.categoryIcon}
          />
          <p className={classes.categoryTitle}>{item.category.title}</p>
        </div>
      </td>
      <td>
        <div className='ml-1'>
          <Improvement improvement={item?.improvement} />
        </div>
      </td>
      <td>
        <p className={classes.date}>{formatDate(item.createdAt)}</p>
      </td>
      <td>
        <p
          className={cs(
            classes.status,
            [classes.pending, item.status.toLowerCase() === 'pending'],
            [classes.rejected, item.status.toLowerCase() === 'rejected'],
            [classes.approved, item.status.toLowerCase() === 'approved'],
          )}
        >
          {item.status}
        </p>
      </td>
      <td>
        <p className='capitalize'>
          {formatName(item?.createdBy)}
        </p>
      </td>
      <td>
        <button type='button' className={classes.button} onClick={() => handleClick(item.id)}>
          View entry
        </button>
      </td>
    </tr>
  );
};

const SurveyTable: React.FC<Props> = ({
  data,
  loading,
  setActiveIndex,
  setShowDetails,
}) => (
  <div className={classes.container}>
    <table className={classes.table}>
      <thead>
        <tr className={classes.tableHeadRow}>
          <th>
            <p className={cs(classes.headingTitle, 'pl-[20px]')}>Survey</p>
          </th>
          <th>
            <p className={classes.headingTitle}>Category</p>
          </th>
          <th>
            <p className={classes.headingTitle}>Condition</p>
          </th>
          <th>
            <p className={classes.headingTitle}>Date</p>
          </th>
          <th>
            <p className={classes.headingTitle}>Status</p>
          </th>
          <th>
            <p className={classes.headingTitle}>Submitted By</p>
          </th>
        </tr>
      </thead>
      <tbody>
        {data.length ? (
          data.map((item: SurveyDataType, index: number) => (
            <SurveyItem
              key={item.id}
              item={item}
              index={index}
              setIndex={setActiveIndex}
              setShowDetails={setShowDetails}
            />
          ))
        ) : (
          <tr>
            <td>
              <p className={classes.notFound}>{loading ? 'Loading ...' : 'No Survey Found'}</p>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default SurveyTable;
